import {RangeDatePicker} from '@/components/commons/range-date-picker/RangeDatePicker'
import {DashboardSearchParams} from '@/features/dashboard/types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {formatDateToSearchParams} from '@/features/medical-records/utils'
import {StyledDashboardFiltersContainer} from './style'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {AssistiveRegimeEnum} from '@/features/projects/types'
import {dashboardFiltersAssistiveRegimeToLabel, dashboardFiltersWorkSectorToLabel} from '../../utils'

export const DashboardFilters = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(DashboardSearchParams)

    const handleSectorChange = (value: keyof typeof dashboardFiltersWorkSectorToLabel) => {
        if (value === 'all') {
            setSearchParams({sector: undefined})
        } else {
            setSearchParams({sector: value as 'outpatient' | 'domiciliary'})
        }
    }

    const handleAssistiveRegimeChange = (value: keyof typeof dashboardFiltersAssistiveRegimeToLabel) => {
        if (value === 'all') {
            setSearchParams({assistiveRegime: undefined})
        } else {
            setSearchParams({assistiveRegime: value as AssistiveRegimeEnum})
        }
    }

    return (
        <StyledDashboardFiltersContainer gap={2}>
            <RangeDatePicker
                values={
                    searchParams.endDate && searchParams.startDate
                        ? {from: new Date(searchParams.startDate), to: new Date(searchParams.endDate)}
                        : {from: undefined, to: undefined}
                }
                onClear={() => setSearchParams({endDate: undefined, startDate: undefined})}
                onConfirm={({from, to}) =>
                    from && to ? setSearchParams(formatDateToSearchParams({startDate: from, endDate: to})) : undefined
                }
            />

            <InputSelect
                style={{width: 'auto'}}
                sameWidth={false}
                value={searchParams.sector || 'all'}
                onClear={searchParams.sector && (() => setSearchParams({sector: undefined}))}
                displayValue={value => {
                    return (
                        <Flexbox gap={1}>
                            <Text palette="neutral" shade="600" size="textSm">
                                {t('commons:workSector')}:
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight={500}>
                                {value}
                            </Text>
                        </Flexbox>
                    )
                }}
                onChange={handleSectorChange}
                optionsMap={dashboardFiltersWorkSectorToLabel}
                placeholder={t('table_filters:select_value')}
            />

            <InputSelect
                style={{width: 'auto'}}
                sameWidth={false}
                value={searchParams.assistiveRegime || 'all'}
                onClear={searchParams.assistiveRegime && (() => setSearchParams({assistiveRegime: undefined}))}
                displayValue={value => {
                    return (
                        <Flexbox gap={1}>
                            <Text palette="neutral" shade="600" size="textSm">
                                {t('commons:welfare_system')}:
                            </Text>
                            <Text palette="neutral" shade="950" size="textSm" weight={500}>
                                {value}
                            </Text>
                        </Flexbox>
                    )
                }}
                onChange={handleAssistiveRegimeChange}
                optionsMap={dashboardFiltersAssistiveRegimeToLabel}
                placeholder={t('table_filters:select_value')}
            />
        </StyledDashboardFiltersContainer>
    )
}
